import { useCallback, useEffect, useState } from 'react';

import {
  applyChoice,
  readStoredChoice,
  resolve,
  type ResolvedTheme,
  type ThemeChoice,
} from './theme';

export interface ThemeApi {
  /** What the visitor asked for, including "follow my device". */
  choice: ThemeChoice;
  /** What that currently resolves to, which is what is on screen. */
  resolved: ResolvedTheme;
  setChoice: (next: ThemeChoice) => void;
  /** Light ⇄ dark from whatever is showing. Leaves "system" behind, deliberately:
   *  pressing a toggle IS expressing a preference. */
  toggle: () => void;
}

export function useTheme(): ThemeApi {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => readStoredChoice());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readStoredChoice()));

  const setChoice = useCallback((next: ThemeChoice) => {
    applyChoice(next);
    setChoiceState(next);
    setResolved(resolve(next));
  }, []);

  // The device changing its mind while the tab is open — a phone crossing into
  // its night schedule. Only followed while the visitor is on "system"; once
  // they have chosen, the choice outranks the device.
  useEffect(() => {
    if (choice !== 'system') return undefined;
    const query = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!query) return undefined;

    const onChange = () => setResolved(query.matches ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [choice]);

  const toggle = useCallback(() => {
    setChoice(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setChoice]);

  return { choice, resolved, setChoice, toggle };
}
